/**
 * Scene 3 — Active-Threat Acoustic Detection.
 *
 * Plays a deterministic 5-second incident timeline. As the timer advances,
 * cards light up in this order:
 *   ambient-shift  → acoustic-detect → stress-spike → triangulation →
 *   silent-route → runbook → approval-request
 *
 * Story-point: detection is acoustic + silent. The system never broadcasts
 * over public talkgroups until a human shift-lead approves.
 *
 * No live audio classification runs in the browser — see scene3-data.ts.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Crosshair,
  EarOff,
  Eye,
  Play,
  RotateCcw,
  ShieldAlert,
  Target,
  Waves,
  XCircle,
} from "lucide-react";

import {
  SCENARIO_EVENTS,
  SCENARIO_TOTAL_MS,
  type ScenarioEvent,
} from "./scene3-data";

type Decision = "pending" | "approved" | "rejected";

const PROGRESS_TICK_MS = 50;

export function SceneIncident() {
  const [phase, setPhase] = useState<"idle" | "playing" | "settled">("idle");
  const [elapsed, setElapsed] = useState(0);
  const [decision, setDecision] = useState<Decision>("pending");
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  const play = useCallback(() => {
    clearTimers();
    setElapsed(0);
    setDecision("pending");
    setPhase("playing");

    const startedAt = performance.now();
    tickRef.current = setInterval(() => {
      const dt = performance.now() - startedAt;
      if (dt >= SCENARIO_TOTAL_MS) {
        setElapsed(SCENARIO_TOTAL_MS);
        setPhase("settled");
        if (tickRef.current) {
          clearInterval(tickRef.current);
          tickRef.current = null;
        }
      } else {
        setElapsed(dt);
      }
    }, PROGRESS_TICK_MS);
  }, [clearTimers]);

  const reset = useCallback(() => {
    clearTimers();
    setPhase("idle");
    setElapsed(0);
    setDecision("pending");
  }, [clearTimers]);

  useEffect(() => clearTimers, [clearTimers]);

  const visibleEvents = SCENARIO_EVENTS.filter((e) => elapsed >= e.t);
  const has = (k: ScenarioEvent["kind"]) => visibleEvents.some((e) => e.kind === k);
  const event = (k: ScenarioEvent["kind"]) =>
    SCENARIO_EVENTS.find((e) => e.kind === k);

  let playLabel: string;
  if (phase === "idle") playLabel = "Play scenario";
  else if (phase === "playing") playLabel = "Playing…";
  else playLabel = "Replay";

  return (
    <div className="space-y-6">
      {/* Header + Controls */}
      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <ShieldAlert className="size-5 text-rose-600 dark:text-rose-400" aria-hidden />
              <h2 className="text-xl font-bold tracking-tight">An incident — silent response</h2>
            </div>
            <p className="mt-1 max-w-2xl text-sm text-slate-500 dark:text-slate-400">
              A simulated 5-second timeline. Acoustic detection, voice-stress
              correlation, location triangulation, and a silent runbook proposed
              to the shift lead. No public-talkgroup broadcast until human approval.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={play}
              disabled={phase === "playing"}
              className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Play className="size-4" aria-hidden />
              {playLabel}
            </button>
            <button
              type="button"
              onClick={reset}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition-all hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <RotateCcw className="size-4" aria-hidden />
              Reset
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
            <span>T+0.0s</span>
            <span>T+{(elapsed / 1000).toFixed(1)}s</span>
            <span>T+{(SCENARIO_TOTAL_MS / 1000).toFixed(1)}s</span>
          </div>
          <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
            <div
              className="h-full bg-rose-500 transition-[width] duration-75 ease-linear"
              style={{ width: `${Math.min(100, (elapsed / SCENARIO_TOTAL_MS) * 100)}%` }}
            />
          </div>
        </div>
      </div>

      {/* Event cards */}
      <div className="grid gap-4 lg:grid-cols-2">
        <EventCard
          active={has("ambient-shift")}
          icon={Waves}
          tone="slate"
          title="Ambient acoustic shift"
          subtitle="T+0.0s · Site acoustic sensors (ceiling-mic array)"
          body="Detected on dedicated PoE ceiling mics 3, 4, 7, 9 — not on radio audio. Machinery hum drops, voice density rises."
        />

        <EventCard
          active={has("acoustic-detect")}
          icon={AlertTriangle}
          tone="rose"
          title="Acoustic class detected"
          subtitle="T+0.4s · YAMNet · 521 classes"
          body={
            has("acoustic-detect")
              ? `Firearm signature — confidence ${(((event("acoustic-detect")!.payload.confidence as number) ?? 0) * 100).toFixed(0)}% · shots: ${event("acoustic-detect")!.payload.shotCount as number}`
              : "—"
          }
        />

        <EventCard
          active={has("stress-spike")}
          icon={Waves}
          tone="amber"
          title="Voice stress spike"
          subtitle="T+1.0s · openSMILE on PTT traffic · k=6 speakers"
          body={
            has("stress-spike")
              ? `${event("stress-spike")!.payload.talkgroup as string} (regular radio traffic) — baseline ${((event("stress-spike")!.payload.baselineScore as number) * 100).toFixed(0)}% → ${((event("stress-spike")!.payload.currentScore as number) * 100).toFixed(0)}%`
              : "—"
          }
        />

        <EventCard
          active={has("triangulation")}
          icon={Crosshair}
          tone="violet"
          title="Multi-mic triangulation"
          subtitle="T+1.5s · hall sensors only"
          body={
            has("triangulation")
              ? `${event("triangulation")!.payload.zone as string} · ±${event("triangulation")!.payload.offsetMeters as number} m · ${event("triangulation")!.payload.micsContributing as number} ceiling mics contributing`
              : "—"
          }
        />

        <EventCard
          active={has("hot-mic")}
          icon={Eye}
          tone="amber"
          title="Hot Mic on Demand (XCMP)"
          subtitle="T+1.9s · post-detection · TX-LED visible · audit-logged"
          body={
            has("hot-mic")
              ? `${event("hot-mic")!.payload.radios as number} nearest radios opened for ${event("hot-mic")!.payload.durationSeconds as number} s · LED ${event("hot-mic")!.payload.ledState as string} · ${event("hot-mic")!.payload.auditEvent as string}. ${event("hot-mic")!.payload.reasoning as string}`
              : "—"
          }
        />

        <EventCard
          active={has("silent-route")}
          icon={EarOff}
          tone="emerald"
          title="Silent route to shift lead"
          subtitle="T+2.7s · public talkgroups suppressed"
          body={
            has("silent-route")
              ? `${event("silent-route")!.payload.audience as string}. ${event("silent-route")!.payload.reasoning as string}`
              : "—"
          }
        />

        <EventCard
          active={has("runbook")}
          icon={Target}
          tone="sky"
          title="Silent runbook (suggested)"
          subtitle="T+3.5s · awaiting human approval"
          body=""
        >
          {has("runbook") && (
            <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-slate-700 dark:text-slate-300">
              {(event("runbook")!.payload.steps as string[]).map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ol>
          )}
        </EventCard>
      </div>

      {/* Approval gate */}
      {has("approval-request") && (
        <ApprovalGate
          decision={decision}
          handle={event("approval-request")!.payload.handle as string}
          note={event("approval-request")!.payload.note as string}
          onApprove={() => setDecision("approved")}
          onReject={() => setDecision("rejected")}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

const TONE_CLS = {
  slate: {
    border: "border-slate-300 dark:border-slate-700",
    iconBg: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
    inactiveBorder: "border-slate-200 dark:border-slate-800",
  },
  rose: {
    border: "border-rose-300 dark:border-rose-500/50",
    iconBg: "bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300",
    inactiveBorder: "border-slate-200 dark:border-slate-800",
  },
  amber: {
    border: "border-amber-300 dark:border-amber-500/50",
    iconBg: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
    inactiveBorder: "border-slate-200 dark:border-slate-800",
  },
  violet: {
    border: "border-violet-300 dark:border-violet-500/50",
    iconBg: "bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-300",
    inactiveBorder: "border-slate-200 dark:border-slate-800",
  },
  emerald: {
    border: "border-emerald-300 dark:border-emerald-500/50",
    iconBg: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
    inactiveBorder: "border-slate-200 dark:border-slate-800",
  },
  sky: {
    border: "border-sky-300 dark:border-sky-500/50",
    iconBg: "bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300",
    inactiveBorder: "border-slate-200 dark:border-slate-800",
  },
} as const;

interface EventCardProps {
  active: boolean;
  icon: React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  tone: keyof typeof TONE_CLS;
  title: string;
  subtitle: string;
  body: string;
  children?: React.ReactNode;
}

function EventCard({
  active, icon: Icon, tone, title, subtitle, body, children,
}: Readonly<EventCardProps>) {
  const t = TONE_CLS[tone];
  return (
    <div
      className={`rounded-2xl border bg-white p-4 transition-all dark:bg-slate-900 ${
        active ? `${t.border} shadow-md` : `${t.inactiveBorder} opacity-50`
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`grid size-10 shrink-0 place-items-center rounded-xl ${t.iconBg}`}>
          <Icon className="size-5" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold tracking-tight">{title}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400">{subtitle}</div>
          {body && <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">{body}</p>}
          {children}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

interface ApprovalGateProps {
  decision: Decision;
  handle: string;
  note: string;
  onApprove: () => void;
  onReject: () => void;
}

function ApprovalGate({ decision, handle, note, onApprove, onReject }: Readonly<ApprovalGateProps>) {
  if (decision === "approved") {
    return (
      <div className="rounded-2xl border border-emerald-300 bg-emerald-50 p-5 dark:border-emerald-500/50 dark:bg-emerald-500/10">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="mt-0.5 size-5 text-emerald-700 dark:text-emerald-400" aria-hidden />
          <div>
            <div className="text-sm font-semibold text-emerald-900 dark:text-emerald-100">
              Evacuation order approved · {handle}
            </div>
            <p className="mt-1 text-sm text-emerald-900/90 dark:text-emerald-100/90">
              Audit event <code className="rounded bg-emerald-200/40 px-1 py-px dark:bg-emerald-500/20">incident::evacuation.approved</code> written.
              Public talkgroups receive structured TTS instruction. EMT staging dispatched.
            </p>
          </div>
        </div>
      </div>
    );
  }
  if (decision === "rejected") {
    return (
      <div className="rounded-2xl border border-slate-300 bg-slate-50 p-5 dark:border-slate-700 dark:bg-slate-800/50">
        <div className="flex items-start gap-3">
          <XCircle className="mt-0.5 size-5 text-slate-600 dark:text-slate-300" aria-hidden />
          <div>
            <div className="text-sm font-semibold">Marked as false positive · {handle}</div>
            <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
              Audit event <code className="rounded bg-slate-200 px-1 py-px dark:bg-slate-700">incident::false_positive</code> written.
              Acoustic model retraining queue updated with this clip and operator label.
            </p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border-2 border-rose-300 bg-rose-50 p-5 dark:border-rose-500/50 dark:bg-rose-500/10">
      <div className="flex items-start gap-3">
        <ShieldAlert className="mt-0.5 size-5 text-rose-700 dark:text-rose-400" aria-hidden />
        <div className="flex-1">
          <div className="text-sm font-semibold text-rose-900 dark:text-rose-100">
            Human approval required · {handle}
          </div>
          <p className="mt-1 text-sm text-rose-900/90 dark:text-rose-100/90">{note}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onApprove}
              className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-rose-700"
            >
              <CheckCircle2 className="size-4" aria-hidden />
              Approve evacuation
            </button>
            <button
              type="button"
              onClick={onReject}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition-all hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <XCircle className="size-4" aria-hidden />
              Mark as false positive
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
